import React, { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Alert, Box, Button, LinearProgress, Paper, Typography } from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import {
  useTable,
  type SortingState,
} from '@tanstack/react-table';
import { features } from '../components/common/tableFeatures';
import { DataTable } from '../components/common/DataTable';
import { dateTime } from '../components/common/tableCells';
import { deploymentTargetColumns } from '../components/deployments/deploymentColumns';
import { KIND_LABEL } from '../components/deployments/labels';
import errorService from '@services/errorService';
import { useGetDeploymentQuery, type DeploymentTarget } from '@store/api/deploymentsApi';

/** Refresh interval while any Device has not reported an Outcome yet. */
export const POLL_MS = 5000;

const KIND_PAGE = {
  client: { path: '/client-updates', label: 'Client updates' },
  dotnet: { path: '/dotnet-app-updates', label: '.NET app updates' },
  dotnet_runtime: { path: '/maintenance', label: 'Maintenance' },
  os_packages: { path: '/maintenance', label: 'Maintenance' },
} as const;
const EMPTY: DeploymentTarget[] = [];

const DeploymentDetailPage: React.FC = () => {
  const { deploymentId = '' } = useParams<{ deploymentId: string }>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [polling, setPolling] = useState(true);
  const { data, isLoading, isError, error } = useGetDeploymentQuery(deploymentId, {
    pollingInterval: polling ? POLL_MS : 0,
  });

  // Once every target has an Outcome nothing changes any more, so stop polling.
  const settled = data !== undefined && data.counts.in_flight === 0;
  if (settled === polling) setPolling(!settled);

  const table = useTable({
    features,
    data: data?.targets ?? EMPTY,
    columns: deploymentTargetColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getRowId: (row) => row.device_id,
  });

  if (isError) {
    return <Alert severity="error">{errorService.toUserMessage(error)}</Alert>;
  }

  const counts = data?.counts;
  const done = counts ? counts.succeeded + counts.failed : 0;

  return (
    <Box>
      {data && (
        <Button
          component={RouterLink}
          nativeButton={false}
          to={KIND_PAGE[data.kind].path}
          startIcon={<BackIcon />}
          size="small"
          sx={{ mb: 1 }}
        >
          {KIND_PAGE[data.kind].label}
        </Button>
      )}
      <Typography variant="h4" component="h1" gutterBottom>
        {data ? `Deployment of ${KIND_LABEL[data.kind]} ${data.version}` : 'Deployment'}
      </Typography>
      {data && counts && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" sx={{
            color: 'text.secondary'
          }}>
            Created {dateTime(data.created_at)} by {data.created_by}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            {counts.succeeded} succeeded, {counts.failed} failed, {counts.in_flight} in progress of{' '}
            {counts.total} device(s)
          </Typography>
          <LinearProgress
            variant="determinate"
            value={counts.total ? (done / counts.total) * 100 : 0}
            color={counts.failed > 0 ? 'warning' : 'primary'}
            sx={{ mt: 1 }}
            aria-label="Deployment progress"
          />
          {counts.in_flight > 0 && (
            <Typography
              variant="caption"
              component="p"
              sx={{
                color: 'text.secondary',
                mt: 1
              }}>
              Refreshing every {POLL_MS / 1000}s. An offline device gets its update when it next
              checks in, unless its command has timed out by then.
            </Typography>
          )}
        </Paper>
      )}
      <Paper sx={{ width: '100%' }}>
        <DataTable
          table={table}
          ariaLabel="Deployment devices"
          isLoading={isLoading}
          emptyMessage="This deployment has no devices."
          minWidth={1000}
        />
      </Paper>
    </Box>
  );
};

export default DeploymentDetailPage;
